// Formation guides: one page per formation available in the game (/formations/4-3-3) plus a
// hub (/formations). Raw facts below; toPage() turns each into the same shape as
// landingPages.js so KeywordLanding renders it and scripts/prerender.mjs bakes it.

const FORMATIONS = [
  {
    shape: '4-4-2', alias: 'the classic flat four',
    summary: 'Two banks of four and two strikers. Simple, balanced and hard to break down, with a strike partnership that always gives the defence two problems.',
    strengths: ['Two strikers occupy both centre-backs', 'Wingers and full-backs double up on each flank', 'Compact, easy-to-hold defensive shape'],
    weaknesses: ['Only two central midfielders, easily outnumbered by a three', 'Gaps between the lines if the midfield is too flat', 'Relies on wide players to create'],
    roles: ['A target striker paired with a quicker runner', 'Two box-to-box central midfielders with high stamina', 'Wingers who can cross and track back'],
    goodVs: '3-5-2 and other back-three systems, where the two strikers and wide pairs exploit the flanks',
    weakVs: '4-3-3 and 4-2-3-1, which overload the centre of midfield',
    tip: 'Use a balanced mentality and medium pressing; push to attacking only when chasing a result, or the midfield two get overrun.',
  },
  {
    shape: '4-3-3', alias: 'the front three',
    summary: 'A midfield three behind a front three. Built for pressing high and attacking with width from the wingers and runners from midfield.',
    strengths: ['Three forwards press the opposition back line', 'Midfield trio controls possession', 'Wide forwards stretch the pitch and cut inside'],
    weaknesses: ['Full-backs exposed if the wingers do not track back', 'Lone striker can be isolated', 'Demands stamina across the front line'],
    roles: ['A holding midfielder to shield the back four', 'Wide forwards with pace and finishing', 'A striker who can hold the ball up or run in behind'],
    goodVs: '4-4-2, where the midfield three outnumbers the central two',
    weakVs: 'teams that sit deep in a 5-4-1 and hit the space behind your full-backs',
    tip: 'Pair it with high pressing and an attacking mentality against weaker teams; drop to balanced away from home.',
  },
  {
    shape: '4-2-3-1', alias: 'the double pivot',
    summary: 'Two holding midfielders, an attacking line of three and a lone striker. The most common shape in modern football because it is solid and creative at once.',
    strengths: ['Double pivot protects the back four', 'Number 10 links midfield and attack', 'Easy to shift between attack and defence'],
    weaknesses: ['Lone striker needs support quickly', 'Wide attackers must defend or the flanks open', 'Can become slow if the 10 is marked out'],
    roles: ['Two defensive midfielders: one ball-winner, one passer', 'A creative attacking midfielder (CAM)', 'A complete striker who can finish and link play'],
    goodVs: '4-4-2 and 4-3-3, thanks to central control',
    weakVs: 'back-three systems with wing-backs that pin your wide attackers',
    tip: 'A safe default: balanced mentality, medium pressing, and your best passer in the pivot.',
  },
  {
    shape: '4-1-4-1', alias: 'the single screen',
    summary: 'One holding midfielder sits in front of the defence with a line of four ahead. Compact without the ball and flexible with it.',
    strengths: ['Very compact defensive block', 'Two central midfielders can join attacks', 'Holding midfielder screens counter-attacks'],
    weaknesses: ['Lone striker often isolated', 'Holding midfielder can be overloaded', 'Needs fit wide midfielders'],
    roles: ['A disciplined defensive midfielder (CDM)', 'Two box-to-box central midfielders', 'A striker strong enough to hold the ball'],
    goodVs: '4-2-3-1, where the CDM marks the opposition 10',
    weakVs: 'two-striker systems that pull the lone CDM out of position',
    tip: 'Good for protecting a lead: switch to a defensive mentality with medium pressing.',
  },
  {
    shape: '4-4-1-1', alias: 'the deep-lying second striker',
    summary: 'A 4-4-2 with one forward dropped into the hole. Keeps the solidity of a flat four while adding a link between midfield and attack.',
    strengths: ['Second striker finds space between the lines', 'Solid two banks of four', 'Easy to switch to a 4-4-2 when chasing'],
    weaknesses: ['Central midfield pair can be outnumbered', 'Striker can be isolated if the 10 drops too deep', 'Limited width beyond the wingers'],
    roles: ['A creative second striker or CAM', 'A strong centre-forward', 'Hard-working wide midfielders'],
    goodVs: '4-3-3, where the second striker sits on their holding midfielder',
    weakVs: '3-5-2, which floods the centre',
    tip: 'Balanced mentality; give your second striker free-kick duties if they have the accuracy.',
  },
  {
    shape: '4-5-1', alias: 'the midfield wall',
    summary: 'Five across midfield and one striker. Built to win the middle of the pitch and frustrate stronger teams.',
    strengths: ['Midfield numbers make you hard to pass through', 'Excellent for protecting a lead', 'Wide midfielders support the striker when attacking'],
    weaknesses: ['Few bodies in the box', 'Striker can be starved of service', 'Can invite too much pressure'],
    roles: ['A striker who can hold the ball and bring others in', 'Hard-working central midfielders', 'Wide players with stamina'],
    goodVs: 'stronger teams playing 4-3-3 or 4-2-3-1',
    weakVs: 'patient teams happy to keep the ball and cross',
    tip: 'Use it away from home or against better squads with a defensive mentality.',
  },
  {
    shape: '4-3-2-1', alias: 'the Christmas tree',
    summary: 'A midfield three, two attacking midfielders tucked inside and a lone striker. Narrow and dangerous through the middle.',
    strengths: ['Overloads the centre of the pitch', 'Two inside forwards support the striker', 'Strong central defensive cover'],
    weaknesses: ['Very little natural width', 'Full-backs must provide all the width', 'Predictable against packed defences'],
    roles: ['Overlapping full-backs with stamina', 'Two creative attacking midfielders', 'A clinical striker'],
    goodVs: '4-4-2, where it overloads the two central midfielders',
    weakVs: 'wide formations that attack your full-backs',
    tip: 'Attacking mentality with high pressing; make sure your full-backs are fit.',
  },
  {
    shape: '4-3-1-2', alias: 'the diamond with two strikers',
    summary: 'Three midfielders, a playmaker at the tip and two strikers. Narrow, direct and built around a number 10.',
    strengths: ['Two strikers plus a 10 in the middle', 'Strong central passing triangles', 'Playmaker gets the ball in dangerous areas'],
    weaknesses: ['No natural width', 'Flanks exposed to overlapping full-backs', 'Playmaker can be marked out'],
    roles: ['A creative CAM', 'A strike partnership with different strengths', 'Full-backs who can attack'],
    goodVs: '4-2-3-1, where you match the centre and outnumber up front',
    weakVs: 'wide 4-4-2 or 3-4-3 shapes',
    tip: 'Balanced to attacking mentality; your 10 should be on corners and free kicks.',
  },
  {
    shape: '4-2-4', alias: 'all-out attack',
    summary: 'Two midfielders and four forwards. Maximum pressure on the opposition defence, at the cost of control.',
    strengths: ['Four attackers pin back the whole defence', 'Lots of crosses and shots', 'Great when chasing a game late on'],
    weaknesses: ['Midfield easily overrun', 'Huge spaces for counter-attacks', 'Exhausting for the central pair'],
    roles: ['Two very fit central midfielders', 'Fast wingers', 'Two strikers who finish well'],
    goodVs: 'deep, defensive teams that you need to break down',
    weakVs: 'any side with a midfield three',
    tip: 'A late-game switch rather than a starting formation: attacking mentality, high pressing and fast transitions when you need a goal.',
  },
  {
    shape: '4-2-2-2', alias: 'the magic square',
    summary: 'Two holding midfielders, two attacking midfielders and two strikers. Strong through the middle with quick transitions.',
    strengths: ['Square in midfield gives passing options', 'Two strikers and two 10s attack together', 'Holding pair protects the defence'],
    weaknesses: ['Little width without attacking full-backs', 'Wide areas exposed', 'Demands disciplined holding midfielders'],
    roles: ['Two defensive midfielders', 'Two attacking midfielders who can shoot', 'Attacking full-backs'],
    goodVs: '4-3-3 sides that struggle with numbers between the lines',
    weakVs: 'wing-back systems that attack the flanks',
    tip: 'Medium pressing and balanced mentality; counter-attacks are its strength.',
  },
  {
    shape: '3-5-2', alias: 'the wing-back system',
    summary: 'Three centre-backs, wing-backs, a midfield three and two strikers. Dominates the middle and uses wing-backs for width.',
    strengths: ['Three centre-backs handle two strikers', 'Midfield three plus wing-backs win the centre', 'Two strikers up front'],
    weaknesses: ['Wing-backs need huge stamina', 'Space behind the wing-backs', 'Vulnerable to wide overloads'],
    roles: ['Fit wing-backs who can cross', 'A ball-playing centre-back', 'A midfield anchor'],
    goodVs: '4-2-3-1 and 4-3-3, where your two strikers face two centre-backs',
    weakVs: '4-4-2 and 3-4-3 sides that double up on your wing-backs',
    tip: 'Rotate wing-backs often; their fitness decides whether this works.',
  },
  {
    shape: '3-4-3', alias: 'the front-three back-three',
    summary: 'Three at the back, four in midfield and three forwards. Aggressive and wide, built to pin teams in their own half.',
    strengths: ['Three forwards press high', 'Wing-backs and wide forwards overload the flanks', 'Lots of attacking width'],
    weaknesses: ['Only two central midfielders', 'Back three exposed on counter-attacks', 'Demanding on the wing-backs'],
    roles: ['Two disciplined central midfielders', 'Wide forwards who cut inside', 'Wing-backs with stamina'],
    goodVs: '4-4-2, where you outnumber them wide',
    weakVs: 'midfield-heavy shapes like 4-5-1 and 3-5-2',
    tip: 'High pressing and attacking mentality at home; it is not a formation for sitting back.',
  },
  {
    shape: '3-5-1-1', alias: 'the back three with a 10',
    summary: 'A back three, midfield five, a number 10 and a lone striker. Solid in the middle with a creator behind the forward.',
    strengths: ['Very strong through the middle', 'Playmaker links midfield and striker', 'Back three gives defensive cover'],
    weaknesses: ['Striker can be isolated', 'Relies heavily on the wing-backs for width', 'Can be slow in attack'],
    roles: ['A creative attacking midfielder', 'A complete striker', 'Wing-backs who can attack and defend'],
    goodVs: '4-2-3-1, matching their midfield and outnumbering the 10',
    weakVs: 'wide 4-3-3 sides that target the space behind wing-backs',
    tip: 'Balanced mentality; switch to 3-5-2 when you need a second striker.',
  },
  {
    shape: '3-4-1-1', alias: 'the compact back three',
    summary: 'Three centre-backs, a flat four, a 10 and a striker. Compact and well-structured with a clear link to the forward.',
    strengths: ['Compact shape that is hard to pass through', 'Playmaker supports the striker', 'Wing-backs give natural width'],
    weaknesses: ['Only one true striker', 'Central pair can be outnumbered', 'Wing-back fitness is critical'],
    roles: ['A playmaker in the hole', 'A target striker', 'Two hard-working central midfielders'],
    goodVs: '4-4-2, with an extra centre-back against two strikers',
    weakVs: 'midfield-three systems',
    tip: 'Medium pressing and a balanced mentality suit it best.',
  },
  {
    shape: '3-2-4-1', alias: 'the box midfield',
    summary: 'A back three, two holding midfielders, a line of four attackers and a striker. Possession-heavy with lots of options in attack.',
    strengths: ['Box midfield controls possession', 'Many attacking options between the lines', 'Back three plus pivot protects counters'],
    weaknesses: ['Complex to balance without strong midfielders', 'Wide attackers must work back', 'Lone striker can be marked out'],
    roles: ['Two calm defensive midfielders', 'Inside forwards who can create and score', 'Ball-playing centre-backs'],
    goodVs: 'deep blocks you need to pass through',
    weakVs: 'fast counter-attacking teams with pace out wide',
    tip: 'Attacking mentality with high pressing when you have the stronger squad.',
  },
  {
    shape: '5-3-2', alias: 'the low block with two strikers',
    summary: 'Five defenders, a midfield three and two strikers. Solid at the back with a real threat on the break.',
    strengths: ['Very hard to break down', 'Two strikers for counter-attacks', 'Midfield three protects the defence'],
    weaknesses: ['Little width in attack beyond the wing-backs', 'Can invite pressure for long spells', 'Wing-backs must defend first'],
    roles: ['Pacey strikers for the counter', 'Disciplined wing-backs', 'A strong defensive midfielder'],
    goodVs: 'stronger attacking teams that leave space behind',
    weakVs: 'patient possession teams with good crossers',
    tip: 'Defensive or cautious mentality with low to medium pressing and fast transitions; let the strikers do the damage on the break.',
  },
  {
    shape: '5-4-1', alias: 'parking the bus',
    summary: 'Five defenders, four midfielders and one striker. The most defensive shape in the game, built to protect a result.',
    strengths: ['Maximum defensive cover', 'Very compact two banks', 'Ideal for holding a lead late on'],
    weaknesses: ['Lone striker gets little support', 'Few goals from open play', 'Hands the initiative to the opponent'],
    roles: ['A striker who can hold the ball alone', 'Defenders strong in the air', 'Hard-working wide midfielders'],
    goodVs: 'much stronger teams when a draw is a good result',
    weakVs: 'teams that are happy to cross and shoot from distance',
    tip: 'Defensive mentality, a low line and slow transitions for the last 15 minutes when you are ahead.',
  },
  {
    shape: '5-2-3', alias: 'the counter-attacking front three',
    summary: 'Five at the back, two central midfielders and three forwards. Defends deep and breaks fast with a front three.',
    strengths: ['Solid five-man defence', 'Three forwards ready for counters', 'Wing-backs can push on to make it a 3-4-3'],
    weaknesses: ['Central midfield pair easily outnumbered', 'Big gap between defence and attack', 'Forwards must work hard without the ball'],
    roles: ['Fast forwards who finish', 'Two tireless central midfielders', 'Wing-backs with pace'],
    goodVs: 'attacking teams that leave space behind their defence',
    weakVs: 'midfield-heavy shapes like 4-5-1 and 3-5-2',
    tip: 'Defensive mentality with medium pressing; switch to attacking once you have the lead to protect.',
  },
];

const listHtml = (items) => `<ul>${items.map((i) => `<li>${i}</li>`).join('')}</ul>`;

const toPage = (f) => ({
  path: `/formations/${f.shape}`,
  lang: 'en',
  shape: f.shape,
  title: `${f.shape} Formation: Strengths, Weaknesses & Best Tactics | My Football Game`,
  description: `How the ${f.shape} formation works: ${f.summary.split('.')[0].toLowerCase()}. Strengths, weaknesses, key roles, what it beats and how to set it up.`,
  eyebrow: `Formation guide · ${f.alias}`,
  h1: `${f.shape} `,
  h1Accent: 'FORMATION',
  subtitle: f.summary,
  breadcrumb: `${f.shape} Formation`,
  sections: [
    { heading: `Strengths of the ${f.shape}`, html: listHtml(f.strengths) },
    { heading: `Weaknesses of the ${f.shape}`, html: listHtml(f.weaknesses) },
    { heading: 'Key roles and players you need', html: listHtml(f.roles) },
    { heading: 'What it beats and what beats it', html: `<p><strong>Works well against:</strong> ${f.goodVs}.</p><p><strong>Struggles against:</strong> ${f.weakVs}.</p>` },
    { heading: `How to set up the ${f.shape} in My Football Game`, html: `<p>${f.tip}</p><p>Put every player in their natural position: players out of position lose effectiveness in the match engine. Then assign your most accurate players to free kicks, corners and penalties.</p>` },
  ],
  faqs: [
    { q: `Is the ${f.shape} a good formation?`, a: `${f.summary} It works best against ${f.goodVs}.` },
    { q: `What beats the ${f.shape}?`, a: `The ${f.shape} usually struggles against ${f.weakVs}.` },
    { q: `What players do you need for the ${f.shape}?`, a: f.roles.join('. ') + '.' },
  ],
  related: ['/formations', '/features/tactics', '/blog/football-manager-formations-guide'],
});

export const formationPages = FORMATIONS.map(toPage);

export const formationsHub = {
  path: '/formations',
  lang: 'en',
  title: 'Football Formations Explained – All 18 Formations & Tactics | My Football Game',
  description: 'Every football formation explained: 4-4-2, 4-3-3, 4-2-3-1, 3-5-2, 5-3-2 and more. Strengths, weaknesses, what each one beats, and how to set it up.',
  eyebrow: 'Tactics library',
  h1: 'FOOTBALL ',
  h1Accent: 'FORMATIONS',
  subtitle: 'All 18 formations in My Football Game, explained: how each one works, where it is strong, where it breaks, and which formations it beats.',
  breadcrumb: 'Formations',
  sections: [
    { heading: 'Back four', html: listHtml(FORMATIONS.filter((f) => f.shape[0] === '4').map((f) => `<a href="/formations/${f.shape}"><strong>${f.shape}</strong></a> — ${f.summary.split('.')[0]}.`)) },
    { heading: 'Back three', html: listHtml(FORMATIONS.filter((f) => f.shape[0] === '3').map((f) => `<a href="/formations/${f.shape}"><strong>${f.shape}</strong></a> — ${f.summary.split('.')[0]}.`)) },
    { heading: 'Back five', html: listHtml(FORMATIONS.filter((f) => f.shape[0] === '5').map((f) => `<a href="/formations/${f.shape}"><strong>${f.shape}</strong></a> — ${f.summary.split('.')[0]}.`)) },
    { heading: 'How to choose a formation', html: `<p>Start from your players, not the shape. If your best players are wingers, pick a system with width (4-3-3, 4-4-2, 3-4-3). If your strength is central midfield, a three or a diamond (4-3-1-2, 3-5-2) gets more of them on the pitch. Then adapt to the opponent: add a holding midfielder against a strong number 10, drop to a back five to protect a lead.</p>` },
  ],
  faqs: [
    { q: 'What is the best formation in football?', a: 'There is no single best formation. The 4-2-3-1 and 4-3-3 are the most balanced starting points, but the best choice depends on your players and your opponent.' },
    { q: 'What is the most defensive formation?', a: 'The 5-4-1 is the most defensive, followed by the 5-3-2. Both are ideal for protecting a lead.' },
    { q: 'What is the most attacking formation?', a: 'The 4-2-4 is the most attacking, with four forwards, but it leaves the midfield badly outnumbered.' },
  ],
  related: ['/features/tactics', '/blog/football-manager-formations-guide', '/how-to-play'],
};
